import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, SmartDataTable, getStatusBadge, Modal, Textarea } from '@/backoffice/components/ui';
import { fetchAdminDisputes, resolveDispute, type AdminDisputeSummary } from '@/domains/admin/services/admin.service';
import { formatRelativeTime } from '@/backoffice/utils';
import { Gavel, AlertCircle, Check, Loader2 } from 'lucide-react';

type FilterStatus = 'all' | 'open' | 'in_review' | 'resolved';

const RESOLUTIONS = [
  { value: 'refund', label: 'Remboursement' },
  { value: 'partial_refund', label: 'Remb. partiel' },
  { value: 'reassign', label: 'Réaffecter' },
  { value: 'no_action', label: 'Pas d\'action' },
];

export function AdminDisputesPage() {
  const [disputes, setDisputes] = useState<AdminDisputeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterStatus>('all');
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState<AdminDisputeSummary | null>(null);
  const [resolution, setResolution] = useState('refund');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    fetchAdminDisputes(activeFilter === 'all' ? undefined : activeFilter)
      .then(setDisputes)
      .catch(() => setDisputes([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [activeFilter]);

  const handleResolve = async () => {
    if (!selectedDispute) return;
    setSaving(true);
    try {
      await resolveDispute(selectedDispute.id, { resolution, resolution_notes: notes });
      setShowResolveModal(false);
      setNotes('');
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="admin" title="Litiges">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="admin" title="Litiges">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-red-50 border-red-200">
          <p className="text-sm text-red-700">Ouverts</p>
          <p className="text-2xl font-bold text-red-900">{disputes.filter((d) => d.status === 'open').length}</p>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <p className="text-sm text-amber-700">En révision</p>
          <p className="text-2xl font-bold text-amber-900">{disputes.filter((d) => d.status === 'in_review').length}</p>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <p className="text-sm text-emerald-700">Résolus</p>
          <p className="text-2xl font-bold text-emerald-900">{disputes.filter((d) => d.status === 'resolved').length}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-700">Total affiché</p>
          <p className="text-2xl font-bold">{disputes.length}</p>
        </Card>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {(['all', 'open', 'in_review', 'resolved'] as FilterStatus[]).map((filter) => (
          <Button key={filter} variant={activeFilter === filter ? 'primary' : 'outline'} size="sm" onClick={() => setActiveFilter(filter)}>
            {filter === 'all' ? 'Tous' : filter === 'open' ? 'Ouverts' : filter === 'in_review' ? 'En révision' : 'Résolus'}
          </Button>
        ))}
      </div>

      <Card>
        <SmartDataTable
          data={disputes}
          columns={[
            { key: 'id', title: 'ID', render: (item) => <span className="font-mono">#{item.id}</span> },
            {
              key: 'client_name',
              title: 'Parties',
              render: (item) => (
                <div>
                  <p className="font-medium">{item.client_name}</p>
                  <p className="text-xs text-gray-500">vs {item.driver_name ?? 'N/A'}</p>
                </div>
              ),
            },
            {
              key: 'reason',
              title: 'Motif',
              render: (item) => (
                <div>
                  <p className="font-medium">{item.reason}</p>
                  <p className="text-xs text-gray-500 truncate max-w-xs">{item.description}</p>
                </div>
              ),
            },
            { key: 'order_code', title: 'Commande', render: (item) => <span className="text-blue-600 font-mono">{item.order_code}</span> },
            { key: 'status', title: 'Statut', render: (item) => getStatusBadge(item.status) },
            { key: 'created_at', title: 'Date', render: (item) => item.created_at ? formatRelativeTime(item.created_at) : '—' },
          ]}
          keyExtractor={(item) => String(item.id)}
          onView={(d) => { setSelectedDispute(d); setShowResolveModal(true); }}
          emptyMessage="Aucun litige"
        />
      </Card>

      <Modal isOpen={showResolveModal} onClose={() => setShowResolveModal(false)} title="Résoudre le litige" size="lg">
        {selectedDispute && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-2">{selectedDispute.reason}</h4>
              <p className="text-sm text-gray-600">{selectedDispute.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {RESOLUTIONS.map((res) => (
                <button
                  key={res.value}
                  type="button"
                  onClick={() => setResolution(res.value)}
                  className={`p-3 border rounded-lg text-sm ${resolution === res.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
                >
                  {res.label}
                </button>
              ))}
            </div>
            <Textarea label="Notes de résolution" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Décrivez la résolution..." />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowResolveModal(false)}>Annuler</Button>
              <Button leftIcon={<Check className="w-4 h-4" />} isLoading={saving} onClick={handleResolve}>Résoudre</Button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
}
